import React, { useState, useEffect } from 'react';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClockRotateLeft,
  faXmark,
  faTrashCan,
  faTriangleExclamation,
  faChevronDown,
  faChevronUp,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';
import {
  getActionHistory,
  getPreviousSessionHistory,
  clearActionHistory,
  clearPersistedHistory,
  ACTION_CATEGORIES,
} from 'modules/App/_actionLogger';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { formatDateTime, DATE_FORMAT } from 'helpers/dateParser';
import { useTranslation } from 'react-i18next';

// Per-type badge colors. Keeps the table scannable — errors red, navigation
// green, requests muted, etc.
const TYPE_STYLES = {
  [ACTION_CATEGORIES.UI_INTERACTION]: 'bg-blue-100 text-blue-700',
  [ACTION_CATEGORIES.INPUT]: 'bg-sky-100 text-sky-700',
  [ACTION_CATEGORIES.ROUTE_CHANGE]: 'bg-emerald-100 text-emerald-700',
  [ACTION_CATEGORIES.HISTORY_CHANGE]: 'bg-emerald-100 text-emerald-700',
  [ACTION_CATEGORIES.API_REQUEST]: 'bg-amber-100 text-amber-700',
  [ACTION_CATEGORIES.INTERNAL_REQUEST]: 'bg-slate-100 text-slate-600',
  [ACTION_CATEGORIES.ERROR]: 'bg-red-100 text-red-700',
  [ACTION_CATEGORIES.LIFECYCLE]: 'bg-violet-100 text-violet-700',
  [ACTION_CATEGORIES.CUSTOM]: 'bg-slate-100 text-slate-600',
};

const FRONTEND_TYPES = [
  ACTION_CATEGORIES.UI_INTERACTION,
  ACTION_CATEGORIES.INPUT,
  ACTION_CATEGORIES.ROUTE_CHANGE,
  ACTION_CATEGORIES.HISTORY_CHANGE,
  ACTION_CATEGORIES.LIFECYCLE,
];
const API_TYPES = [ACTION_CATEGORIES.API_REQUEST, ACTION_CATEGORIES.INTERNAL_REQUEST];

const thClass =
  'text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 select-none px-3 py-2 text-left';

// Combine a crash entry's JS stack and React component stack into one block.
function buildStackText(meta) {
  if (!meta) return '';
  const parts = [];
  if (meta.stack) parts.push(meta.stack);
  if (meta.componentStack) parts.push(`Component stack:${meta.componentStack}`);
  return parts.join('\n\n').trim();
}

// Radix single-select segmented control, styled shadcn-style.
function Segmented({ value, onChange, options }) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(v) => v && onChange(v)}
      className="inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1"
    >
      {options.map((opt) => (
        <ToggleGroup.Item
          key={opt.value}
          value={opt.value}
          className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 data-[state=on]:bg-white data-[state=on]:shadow-sm ${
            opt.danger ? 'data-[state=on]:text-red-600' : 'data-[state=on]:text-primaryColor'
          }`}
        >
          {opt.label}
          {opt.count != null && (
            <span className="text-[10px] font-semibold opacity-70">{opt.count}</span>
          )}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}

const ActionHistoryViewer = () => {
  const { t } = useTranslation('common');
  const [actionHistory, setActionHistory] = useState([]);
  const [prevHistory, setPrevHistory] = useState([]);
  const [showPrevious, setShowPrevious] = useState(false); // show pre-crash session
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // Filter for action types
  const [expanded, setExpanded] = useState({}); // expanded stack traces, keyed by row

  const toggleExpanded = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const copyText = (text) => {
    try {
      navigator.clipboard?.writeText(text);
    } catch {
      // clipboard blocked — ignore
    }
  };

  // Surface a pre-crash session indicator even while collapsed.
  useEffect(() => {
    setPrevHistory(getPreviousSessionHistory());
  }, []);

  // Poll the live history every second while open.
  useEffect(() => {
    if (!isOpen) return;

    const updateHistory = () => setActionHistory(getActionHistory());
    updateHistory();
    setPrevHistory(getPreviousSessionHistory());

    const interval = setInterval(updateHistory, 1000);
    return () => clearInterval(interval);
  }, [isOpen]);

  const handleClearHistory = () => {
    if (showPrevious) {
      clearPersistedHistory();
      setPrevHistory([]);
      setShowPrevious(false);
    } else {
      clearActionHistory();
      setActionHistory([]);
    }
  };

  // The active dataset: live session, or the previous (pre-crash) session.
  const sourceActions = showPrevious ? prevHistory : actionHistory;

  const filteredActions = (() => {
    if (filter === 'all') return sourceActions;
    if (filter === 'frontend') return sourceActions.filter((a) => FRONTEND_TYPES.includes(a.type));
    if (filter === 'api') return sourceActions.filter((a) => API_TYPES.includes(a.type));
    return sourceActions.filter((a) => a.type === filter);
  })();

  if (!isOpen) {
    return (
      <CgRadixButton
        variant="primary"
        size="sm"
        icon={faClockRotateLeft}
        onClick={() => setIsOpen(true)}
        className="relative shadow-md"
      >
        {t('history.title', 'Action History')}
        {prevHistory.length > 0 && (
          <span
            className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow"
            title={t(
              'common:history.actionsCapturedBeforeCrash',
              '{{count}} actions captured before the last reload/crash',
              { count: prevHistory.length },
            )}
          >
            {prevHistory.length}
          </span>
        )}
      </CgRadixButton>
    );
  }

  return (
    <div className="flex max-h-[80vh] w-[460px] max-w-[92vw] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={faClockRotateLeft}
            className="text-primaryColor"
          />
          <span className="text-sm font-semibold text-slate-800">
            {t('history.title', 'Action History')}
          </span>
        </div>
        <CgRadixButton
          variant="ghost"
          size="sm"
          icon={faXmark}
          aria-label={t('common:actions.close', 'Close')}
          onClick={() => setIsOpen(false)}
          className="!px-2 text-slate-400 hover:text-slate-600"
        />
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-2 border-b border-slate-100 px-4 py-2.5">
        {prevHistory.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {t('history.session', 'Session')}
            </span>
            <Segmented
              value={showPrevious ? 'prev' : 'live'}
              onChange={(v) => setShowPrevious(v === 'prev')}
              options={[
                {
                  value: 'live',
                  label: t('actionHistory.live', 'Live'),
                  count: actionHistory.length,
                },
                {
                  value: 'prev',
                  label: t('actionHistory.beforeLastCrash', 'Before last crash'),
                  count: prevHistory.length,
                  danger: true,
                },
              ]}
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            {t('fields.type', 'Type')}
          </span>
          <Segmented
            value={filter === 'all' || filter === 'frontend' || filter === 'api' ? filter : 'all'}
            onChange={setFilter}
            options={[
              { value: 'all', label: t('actionHistory.filterAll', 'All') },
              { value: 'frontend', label: t('actionHistory.frontend', 'Front-end') },
              { value: 'api', label: t('actionHistory.api', 'API') },
            ]}
          />
        </div>
      </div>

      {/* Table */}
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="min-w-full border-separate border-spacing-0">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className={`${thClass} w-[72px]`}>{t('history.time', 'Time')}</th>
              <th className={`${thClass} w-[120px]`}>{t('fields.type', 'Type')}</th>
              <th className={thClass}>{t('history.description', 'Description')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredActions.length === 0 ? (
              <tr>
                <td
                  colSpan="3"
                  className="px-3 py-6 text-center text-sm text-slate-400"
                >
                  {t('history.none', 'No actions recorded yet')}
                </td>
              </tr>
            ) : (
              filteredActions.map((action, index) => {
                const isError = action.type === ACTION_CATEGORIES.ERROR;
                const meta = action.meta;
                const stackText = buildStackText(meta);
                const hasStack = isError && !!stackText;
                const key = `${action.timestamp}-${index}`;
                const isExpanded = !!expanded[key];
                return (
                  <React.Fragment key={key}>
                    <tr
                      className={`border-b border-slate-50 ${isError ? 'bg-red-50' : 'hover:bg-slate-50'}`}
                    >
                      <td className="whitespace-nowrap px-3 py-1.5 align-top font-mono text-[11px] text-slate-400">
                        {formatDateTime(action.timestamp, 'LT')}
                      </td>
                      <td className="px-3 py-1.5 align-top">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            TYPE_STYLES[action.type] || 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {action.type}
                        </span>
                      </td>
                      <td
                        className={`px-3 py-1.5 align-top text-xs ${
                          isError
                            ? 'font-semibold text-red-700'
                            : action.type.includes('request')
                              ? 'text-slate-500'
                              : 'font-medium text-slate-700'
                        }`}
                      >
                        <span className="flex items-start gap-1.5">
                          {isError && (
                            <FontAwesomeIcon
                              icon={faTriangleExclamation}
                              className="mt-0.5 shrink-0 text-red-500"
                            />
                          )}
                          <span className="break-all">{action.description}</span>
                          {hasStack && (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(key)}
                              className="ml-auto shrink-0 rounded p-0.5 text-red-400 hover:bg-red-100 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/40"
                              aria-label={
                                isExpanded
                                  ? t('common:history.hideStackTrace', 'Hide stack trace')
                                  : t('common:history.showStackTrace', 'Show stack trace')
                              }
                              title={
                                isExpanded
                                  ? t('common:history.hideStackTrace', 'Hide stack trace')
                                  : t('common:history.showStackTrace', 'Show stack trace')
                              }
                            >
                              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                    {hasStack && isExpanded && (
                      <tr>
                        <td
                          colSpan="3"
                          className="bg-slate-900 px-3 py-2"
                        >
                          <div className="mb-1 flex items-center justify-between">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                              {meta.source
                                ? t(
                                    'common:history.stackTraceWithSource',
                                    'Stack trace · {{source}}',
                                    {
                                      source: meta.source,
                                    },
                                  )
                                : t('common:history.stackTrace', 'Stack trace')}
                            </span>
                            <CgRadixButton
                              variant="ghost"
                              size="sm"
                              icon={faCopy}
                              onClick={() => copyText(stackText)}
                              className="!py-0.5 text-slate-300 hover:bg-slate-700 hover:text-white"
                            >
                              {t('common:actions.copy', 'Copy')}
                            </CgRadixButton>
                          </div>
                          <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] leading-relaxed text-slate-200">
                            {stackText}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5">
        <span className="text-xs text-slate-400">
          {showPrevious
            ? t('common:history.countOfTotalPreCrash', '{{count}} of {{total}} pre-crash actions', {
                count: filteredActions.length,
                total: sourceActions.length,
              })
            : t('common:history.countOfTotalLive', '{{count}} of {{total}} live actions', {
                count: filteredActions.length,
                total: sourceActions.length,
              })}
        </span>
        <CgRadixButton
          variant="ghost"
          size="sm"
          icon={faTrashCan}
          onClick={handleClearHistory}
          className="border border-red-200 text-red-600 hover:bg-red-50"
        >
          {showPrevious
            ? t('common:history.clearPreCrash', 'Clear pre-crash')
            : t('common:actions.clear', 'Clear')}
        </CgRadixButton>
      </div>
    </div>
  );
};

export default ActionHistoryViewer;
